import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";

const PAGE_SIZE = 10;
const STATUSES = ["All", "Active", "Onboarding", "Inactive", "Exit"];
const EMPTY_FORM = {
  employeeCode: "",
  fullName: "",
  email: "",
  phone: "",
  department: "",
  designation: "",
  employmentType: "Full Time",
  reportingManager: "",
  location: "",
  status: "Active",
  joiningDate: "",
};

function toDateInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function StatusBadge({ status }) {
  const tone =
    status === "Active" ? "success" :
    status === "Onboarding" ? "warn" :
    status === "Exit" ? "danger" :
    "neutral";

  return <span className={`hrms-badge ${tone}`}>{status}</span>;
}

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (status !== "All") params.set("status", status);
      const res = await apiFetch(`/api/hrms/employees?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load employees.");
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status]);

  const totalPages = Math.max(1, Math.ceil(employees.length / PAGE_SIZE));
  const pagedEmployees = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return employees.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [employees, page, totalPages]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  }

  function openEdit(employee) {
    setEditing(employee);
    setForm({
      employeeCode: employee.employeeCode || "",
      fullName: employee.fullName || "",
      email: employee.email || "",
      phone: employee.phone || "",
      department: employee.department || "",
      designation: employee.designation || "",
      employmentType: employee.employmentType || "Full Time",
      reportingManager: employee.reportingManager || "",
      location: employee.location || "",
      status: employee.status || "Active",
      joiningDate: toDateInput(employee.joiningDate),
    });
    setModalOpen(true);
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function saveEmployee(event) {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const payload = {
        ...form,
        joiningDate: form.joiningDate ? `${form.joiningDate}T00:00:00` : null,
      };
      const res = await apiFetch(
        editing ? `/api/hrms/employees/${editing.id}` : "/api/hrms/employees",
        {
          method: editing ? "PUT" : "POST",
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err?.message || "Could not save employee.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="hrms-page">
      <PageHeader
        title="Employees"
        subtitle={`${employees.length} employee${employees.length === 1 ? "" : "s"} in the current view.`}
        actions={<button className="primary" onClick={openCreate}>Add Employee</button>}
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, code, department, phone"
            aria-label="Search employees"
          />
        </div>
        <div className="crm-stage-filter" aria-label="Employee status">
          {STATUSES.map((item) => (
            <button
              key={item}
              type="button"
              className={status === item ? "active" : ""}
              onClick={() => setStatus(item)}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card hrms-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>
            {[1, 2, 3, 4, 5].map((item) => (
              <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />
            ))}
          </div>
        ) : employees.length === 0 ? (
          <EmptyState
            title={search || status !== "All" ? "No employees match your filters" : "No employees yet"}
            subtitle={search || status !== "All" ? "Clear the filters and try again." : "Add the first employee to start HRMS workflows."}
            action={!search && status === "All" ? <button className="primary" onClick={openCreate}>Add Employee</button> : null}
          />
        ) : (
          <>
            <div className="hrms-table-wrap">
              <table className="hrms-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Code</th>
                    <th>Department</th>
                    <th>Contact</th>
                    <th>Type</th>
                    <th>Joining</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedEmployees.map((employee) => (
                    <tr key={employee.id}>
                      <td>
                        <div className="hrms-person-cell">
                          <div className="avatar">{employee.fullName?.[0]?.toUpperCase() || "E"}</div>
                          <div>
                            <div className="hrms-row-title">{employee.fullName}</div>
                            <div className="hrms-row-subtitle">{employee.designation || "Employee"}</div>
                          </div>
                        </div>
                      </td>
                      <td>{employee.employeeCode}</td>
                      <td>{employee.department || "-"}</td>
                      <td>
                        <div>{employee.phone || "-"}</div>
                        <div className="hrms-row-subtitle">{employee.email || ""}</div>
                      </td>
                      <td>{employee.employmentType}</td>
                      <td>{formatDate(employee.joiningDate)}</td>
                      <td><StatusBadge status={employee.status} /></td>
                      <td>
                        <button className="btn-ghost" onClick={() => openEdit(employee)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={Math.min(page, totalPages)}
              totalPages={totalPages}
              onPageChange={setPage}
              total={employees.length}
              pageSize={PAGE_SIZE}
            />
          </>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <form className="glass-card modal crm-modal" onSubmit={saveEmployee} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? "Edit Employee" : "Add Employee"}</h3>
              <button type="button" className="modal-close" onClick={() => setModalOpen(false)} aria-label="Close">
                x
              </button>
            </div>
            <div className="crm-form-grid">
              <label>
                Employee Code
                <input value={form.employeeCode} onChange={(event) => updateField("employeeCode", event.target.value)} placeholder="Auto if blank" />
              </label>
              <label>
                Full Name
                <input value={form.fullName} onChange={(event) => updateField("fullName", event.target.value)} required />
              </label>
              <label>
                Email
                <input type="email" value={form.email} onChange={(event) => updateField("email", event.target.value)} />
              </label>
              <label>
                Phone
                <input value={form.phone} onChange={(event) => updateField("phone", event.target.value)} />
              </label>
              <label>
                Department
                <input value={form.department} onChange={(event) => updateField("department", event.target.value)} />
              </label>
              <label>
                Designation
                <input value={form.designation} onChange={(event) => updateField("designation", event.target.value)} />
              </label>
              <label>
                Employment Type
                <select value={form.employmentType} onChange={(event) => updateField("employmentType", event.target.value)}>
                  <option>Full Time</option>
                  <option>Contract</option>
                  <option>Intern</option>
                  <option>Consultant</option>
                </select>
              </label>
              <label>
                Status
                <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
                  {STATUSES.filter((item) => item !== "All").map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </label>
              <label>
                Reporting Manager
                <input value={form.reportingManager} onChange={(event) => updateField("reportingManager", event.target.value)} />
              </label>
              <label>
                Location
                <input value={form.location} onChange={(event) => updateField("location", event.target.value)} />
              </label>
              <label>
                Joining Date
                <input type="date" value={form.joiningDate} onChange={(event) => updateField("joiningDate", event.target.value)} />
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Save Employee"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
